import { contentFetch } from "../contentClient.js";
import {
    resolveScopedIdArgs,
    resolveScopedLinkArgs,
    resolveScopedLinkPayloadArgs,
    resolveScopedListArgs,
    resolveScopedMutationArgs,
    resolveScopedPayloadArgs,
    resolveScopedRenameArgs,
    resolveScopedValueArgs,
} from "./scope.js";

// ============= Generic Entities API (v2) =============
// Used when entity type is not hardcoded (e.g. unified list page)

const base = (projectId, versionId) =>
    `/api/v2/projects/${projectId}/versions/${versionId}/entities`;

export async function getEntities(
    { projectId, versionId, entityType, search, page = 1, pageSize, sort, lang, include } = {},
    init,
) {
    ({ args: { projectId, versionId, entityType, search, page, pageSize, sort, lang, include }, init } =
        resolveScopedListArgs({ projectId, versionId, entityType, search, page, pageSize, sort, lang, include }, init));
    const params = { page };
    if (pageSize) params.pageSize = pageSize;
    if (entityType) params["filter.entityType"] = entityType;
    if (search?.trim()) params["filter.systemName:contains"] = search.trim();
    if (sort) params.sort = sort;
    if (lang) params.lang = lang;
    if (include) params.include = include;
    return contentFetch(base(projectId, versionId), { params, ...(init || {}) });
}

export async function getEntityById(projectId, versionId, id, init) {
    ({ projectId, versionId, id, init } = resolveScopedIdArgs(projectId, versionId, id, init));
    return contentFetch(`${base(projectId, versionId)}/${id}`, init);
}

export async function createEntity(projectId, versionId, payload, init) {
    ({ projectId, versionId, payload, init } = resolveScopedPayloadArgs(projectId, versionId, payload, init));
    // payload: { systemName, name, description?, entityType }
    return contentFetch(base(projectId, versionId), {
        method: "POST",
        body: payload,
        ...(init || {}),
    });
}

export async function patchEntity(projectId, versionId, id, payload, init) {
    ({ projectId, versionId, id, payload, init } = resolveScopedMutationArgs(projectId, versionId, id, payload, init));
    return contentFetch(`${base(projectId, versionId)}/${id}`, {
        method: "PATCH",
        body: payload,
        ...(init || {}),
    });
}

export async function renameEntitySystemName(projectId, versionId, id, newSystemName, init) {
    ({ projectId, versionId, id, newSystemName, init } = resolveScopedRenameArgs(projectId, versionId, id, newSystemName, init));
    return contentFetch(`${base(projectId, versionId)}/${id}:rename`, {
        method: "POST",
        body: { newSystemName },
        ...(init || {}),
    });
}

export async function deleteEntity(projectId, versionId, id, init) {
    ({ projectId, versionId, id, init } = resolveScopedIdArgs(projectId, versionId, id, init));
    return contentFetch(`${base(projectId, versionId)}/${id}`, {
        method: "DELETE",
        ...(init || {}),
    });
}

// --- Entity values ---

export async function getEntityValues(projectId, versionId, id, init) {
    ({ projectId, versionId, id, init } = resolveScopedIdArgs(projectId, versionId, id, init));
    return contentFetch(`${base(projectId, versionId)}/${id}/values`, init);
}

export async function setEntityValue(projectId, versionId, id, parameterId, payload, init) {
    ({ projectId, versionId, id, parameterId, payload, init } = resolveScopedValueArgs(projectId, versionId, id, parameterId, payload, init));
    return contentFetch(`${base(projectId, versionId)}/${id}/values/${parameterId}`, {
        method: "PUT",
        body: payload,
        ...(init || {}),
    });
}

export async function deleteEntityValue(projectId, versionId, id, parameterId, init) {
    ({ projectId, versionId, id, parameterId, init } = resolveScopedValueArgs(projectId, versionId, id, parameterId, undefined, init));
    return contentFetch(`${base(projectId, versionId)}/${id}/values/${parameterId}`, {
        method: "DELETE",
        ...(init || {}),
    });
}

// --- Entity parameter groups ---

export async function getEntityParameterGroups(projectId, versionId, id, init) {
    ({ projectId, versionId, id, init } = resolveScopedIdArgs(projectId, versionId, id, init));
    return contentFetch(`${base(projectId, versionId)}/${id}/parameter-groups`, init);
}

export async function attachParameterGroupToEntity(projectId, versionId, id, payload, init) {
    ({ projectId, versionId, id, payload, init } = resolveScopedMutationArgs(projectId, versionId, id, payload, init));
    return contentFetch(`${base(projectId, versionId)}/${id}/parameter-groups`, {
        method: "POST",
        body: payload,
        ...(init || {}),
    });
}

export async function patchEntityParameterGroupLink(projectId, versionId, id, linkId, payload, init) {
    ({ projectId, versionId, id, linkId, payload, init } = resolveScopedLinkPayloadArgs(projectId, versionId, id, linkId, payload, init));
    return contentFetch(`${base(projectId, versionId)}/${id}/parameter-groups/${linkId}`, {
        method: "PATCH",
        body: payload,
        ...(init || {}),
    });
}

export async function detachParameterGroupFromEntity(projectId, versionId, id, linkId, init) {
    ({ projectId, versionId, id, linkId, init } = resolveScopedLinkArgs(projectId, versionId, id, linkId, init));
    return contentFetch(`${base(projectId, versionId)}/${id}/parameter-groups/${linkId}`, {
        method: "DELETE",
        ...(init || {}),
    });
}
